import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { switchMap, filter } from 'rxjs/operators';
import { Notification } from '../models/notification.model';
import { NotificationForm } from '../forms/notification.form';
import { NOTIFICATIONS, UpdateNotification, NotificationsUpdated } from '../state-mgmt';
import { UserService } from './user.service';
import { NotificationsClient } from '../clients/notifications.client';

/**
 * The notification service is used by the UI components to perform specific actions on a notification.
 * UI components should interact with this instead of the store or clients directly so that these implementations can
 * be reused by other components.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private authService = inject(UserService);
  private store = inject<Store<any>>(Store);
  private notificationClient = inject(NotificationsClient);

  private readonly notifications$: Observable<Notification[]>;

  constructor() {
    // this.store.dispatch(new LoadNotifications());

    // Only request notifications if user is logged in
    this.notifications$ = this.authService.currentUser.pipe(
      filter((user) => user != null),
      switchMap((user) => this.store.select(NOTIFICATIONS)),
    );
  }

  /**
   * Returns an observable of the notifications that are stored in the current state management.
   */
  get(): Observable<Notification[]> {
    return this.notifications$;
  }

  /**
   * Marks the notifications as read and dispatches and update event.
   */
  markAsRead(notification: Notification) {
    if (notification.isRead) { return; }

    notification.isRead = true;
    this.store.dispatch(UpdateNotification({ form: new NotificationForm(notification) }));
  }

  /**
   * Marks the notifications as read and dispatches and update event.
   */
  async markAllAsRead(notificationsIds: string[]) {
    // Call API to mark all notifications as read.
    await this.notificationClient.markAsRead(notificationsIds).toPromise();

    // Dispatch to store to now reload the notifications in the UI
    this.store.dispatch(NotificationsUpdated({ notifications: [] }));
  }

  /**
   * Marks the notifications as read using userId and dispatches and update event.
   */
  async markAllNotificationsAsRead(userId: string) {
    // Call API to mark all notifications as read.
    await this.notificationClient.markAllNotificationsAsRead(userId).toPromise();

    // Dispatch to store to now reload the notifications in the UI
    this.store.dispatch(NotificationsUpdated({ notifications: [] }));
  }
}
